import {
  createRootRoute,
  Outlet,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useEffect, useState } from 'react';
import { Role } from '@momentum/types';

import { initializeI18n } from '@/i18n';
import { useLanguageDirection } from '@/shared/hooks';

import { DesktopMenu } from '@/shared/navigation/DesktopMenu';
import { MobileBottomNav } from '@/shared/navigation/MobileBottomNav';
import { Toaster } from 'sonner';
import { PostHogPageView } from '@/shared/providers/PostHogProvider';

const ADMIN_ALLOWED_PATHS = new Set<string>([
  '/integrations',
  '/terms-of-use',
  '/privacy-policy',
  '/deal-intelligence',
  '/get-started',
  '/roi-demo',
  '/gap-review',
]);

const ADMIN_ALLOWED_PATH_PREFIXES = [
  '/roi-demo/',
  '/deal-intelligence/',
  '/gap-review/',
];

function isAdminAllowedPath(pathname: string): boolean {
  if (ADMIN_ALLOWED_PATHS.has(pathname)) {
    return true;
  }
  return ADMIN_ALLOWED_PATH_PREFIXES.some((prefix) =>
    pathname.startsWith(prefix)
  );
}

const CHROMELESS_PATHS = new Set<string>([
  '/onboarding',
  '/breaks',
  '/command-center',
  '/project',
  '/leadership',
  '/retrospectives',
  '/effectiveness',
  '/get-started',
  '/integrations',
  '/deal-intelligence',
  '/gap-review',
]);

const CHROMELESS_PATH_PREFIXES = ['/deal-intelligence/', '/gap-review/'];

function isChromelessPath(pathname: string): boolean {
  if (CHROMELESS_PATHS.has(pathname)) {
    return true;
  }
  return CHROMELESS_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

const ONBOARDING_EXEMPT_PATHS = new Set<string>([
  '/onboarding',
  '/get-started',
]);

function isOnboardingExemptPath(pathname: string): boolean {
  return ONBOARDING_EXEMPT_PATHS.has(pathname);
}

// Paths a user without a company may request; instead of the full onboarding
// flow they get the lightweight get-started gate, which completes onboarding
// and then forwards them to the path they originally requested.
const GET_STARTED_GATED_PATHS = new Set<string>(['/deal-intelligence']);

const GET_STARTED_GATED_PATH_PREFIXES = ['/deal-intelligence/'];

function isGetStartedGatedPath(pathname: string): boolean {
  if (GET_STARTED_GATED_PATHS.has(pathname)) {
    return true;
  }
  return GET_STARTED_GATED_PATH_PREFIXES.some((prefix) =>
    pathname.startsWith(prefix)
  );
}

const ADMIN_ROLES = new Set<string>([Role.admin, Role.system_admin]);

initializeI18n();

export const Route = createRootRoute({
  component: RootComponent,
});

function RootComponent() {
  // This hook will automatically update the HTML dir and lang attributes
  useLanguageDirection();

  const { userId, isLoaded, sessionClaims, orgRole } = useAuth();
  const navigate = useNavigate();
  const { location } = useRouterState();
  const [ready, setReady] = useState(false);
  const [isPublicPage, setIsPublicPage] = useState(true);
  const isChromeless = isChromelessPath(location.pathname);

  useEffect(() => {
    if (!isLoaded) {
      return;
    }
    const publicPaths = ['/signin', '/terms-of-use', '/privacy-policy'];
    const isSignInPage = location.pathname === '/signin';
    const isPublicPage = publicPaths.includes(location.pathname);
    const isAdmin = !!orgRole && ADMIN_ROLES.has(orgRole);

    if (userId && isAdmin && !isAdminAllowedPath(location.pathname)) {
      navigate({ to: '/integrations', replace: true });
    } else if (userId && isSignInPage) {
      navigate({ to: '/', replace: true });
    } else if (
      userId &&
      !sessionClaims.metadata.companyId &&
      !isOnboardingExemptPath(location.pathname)
    ) {
      if (isGetStartedGatedPath(location.pathname)) {
        navigate({
          to: '/get-started',
          search: { redirect: location.pathname },
          replace: true,
        });
      } else {
        navigate({ to: '/onboarding', replace: true });
      }
    } else if (!userId && !isPublicPage) {
      navigate({ to: '/signin', replace: true });
    }

    setReady(true);
    setIsPublicPage(isPublicPage);
  }, [isLoaded, userId, orgRole, location.pathname, sessionClaims, navigate]);

  if (!isLoaded || !ready) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p>Loading...</p>
        <PostHogPageView />
      </div>
    );
  }

  return (
    <>
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: 'var(--background)',
            color: 'var(--foreground)',
            border: '1px solid var(--border)',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
        }}
      />
      <div className="h-screen flex flex-col bg-background">
        <div className="flex flex-1 overflow-hidden">
          {!isChromeless && (
            <aside className="max-md:hidden min-w-[220px] overflow-y-auto text-white bg-gradient-to-b from-[#0E1B3A] via-[#152A52] to-[#1C3870] border-b border-white/10">
              <DesktopMenu isPublic={isPublicPage} />
            </aside>
          )}
          <main className="flex-1 overflow-y-auto main-content-with-bottom-nav bg-page-bg">
            <Outlet />
          </main>
        </div>
        {!isChromeless && <MobileBottomNav />}
      </div>
      <PostHogPageView />
    </>
  );
}
